/**
 * Persoenliche Todos-Listen-Page — Phase 2 von Todos-Notifications-Plan.
 *
 * URL: /todos
 *
 * Phase 2 = nur persoenliche Sicht. Senden an andere (Quittierung) +
 * Gruppen-Sichtbarkeit kommen in Phase 3.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Check, Trash2, Calendar, AlertCircle, Loader2, Pencil, X, Inbox, Send, Bell } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

import TodoQuickCaptureModal from '../components/TodoQuickCaptureModal';

type TodoStatus = 'open' | 'in_progress' | 'done' | 'cancelled';
type AssignmentState = 'self' | 'pending' | 'accepted' | 'rejected';

interface Todo {
  id: string;
  title: string;
  description: string | null;
  status: TodoStatus;
  owner_user_id: string;
  assignee_user_id: string | null;
  assignment_state: AssignmentState;
  rejected_reason: string | null;
  due_at: string | null;
  reminder_at: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

type FilterKey = 'active' | 'today' | 'week' | 'done' | 'incoming' | 'sent';

const FILTERS: { key: FilterKey; label: string; query: string; icon?: typeof Inbox }[] = [
  { key: 'active',   label: 'Offen',       query: 'mode=mine&status=active' },
  { key: 'today',    label: 'Heute',       query: 'mode=mine&status=active&due=today' },
  { key: 'week',     label: 'Diese Woche', query: 'mode=mine&status=active&due=week' },
  { key: 'done',     label: 'Erledigt',    query: 'mode=mine&status=done' },
  { key: 'incoming', label: 'Eingehend',   query: 'mode=incoming', icon: Inbox },
  { key: 'sent',     label: 'Gesendet',    query: 'mode=sent', icon: Send },
];

export default function TodosPage() {
  const { user } = useAuth();
  const myId = user?.id ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter: FilterKey = useMemo(() => {
    const mode = searchParams.get('mode');
    if (mode === 'incoming') return 'incoming';
    if (mode === 'sent') return 'sent';
    return 'active';
  }, [searchParams]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [filter, setFilter] = useState<FilterKey>(initialFilter);
  const [incomingCount, setIncomingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDue, setEditDue] = useState('');
  const [editReminder, setEditReminder] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const q = FILTERS.find((f) => f.key === filter)?.query ?? '';
      const [list, cnt] = await Promise.all([
        api.get<{ success: boolean; todos: Todo[] }>(`/todos?${q}`),
        api.get<{ success: boolean; count: number }>(`/todos/incoming-count`),
      ]);
      setTodos(list.todos ?? []);
      setIncomingCount(cnt.count ?? 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  // Tab-Wechsel: URL-Param ?mode= mit-aktualisieren (Notifications linken hierhin).
  useEffect(() => {
    const targetMode = filter === 'incoming' ? 'incoming' : filter === 'sent' ? 'sent' : null;
    const current = searchParams.get('mode');
    if (current !== targetMode) {
      const next = new URLSearchParams(searchParams);
      if (targetMode) next.set('mode', targetMode);
      else next.delete('mode');
      setSearchParams(next, { replace: true });
    }
  }, [filter, searchParams, setSearchParams]);

  const accept = async (id: string): Promise<void> => {
    try {
      await api.post(`/todos/${id}/accept`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const reject = async (id: string): Promise<void> => {
    const reason = prompt('Ablehnungs-Grund (optional)?') ?? '';
    try {
      await api.post(`/todos/${id}/reject`, { reason });
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggle = async (id: string): Promise<void> => {
    try {
      const r = await api.post<{ success: boolean; todo: Todo }>(`/todos/${id}/toggle`);
      setTodos((prev) => prev.map((t) => t.id === id ? r.todo : t));
      // Wenn der aktuelle Filter den neuen Status nicht mehr matched, reload.
      if (filter !== 'done' && r.todo.status === 'done') void load();
      else if (filter === 'done' && r.todo.status !== 'done') void load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const del = async (id: string): Promise<void> => {
    if (!confirm('Todo wirklich loeschen?')) return;
    try {
      await api.delete(`/todos/${id}`);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const startEdit = (t: Todo): void => {
    setEditId(t.id);
    setEditTitle(t.title);
    setEditDesc(t.description ?? '');
    setEditDue(t.due_at ? toLocalInputValue(t.due_at) : '');
    setEditReminder(t.reminder_at ? toLocalInputValue(t.reminder_at) : '');
  };

  const cancelEdit = (): void => {
    setEditId(null);
    setEditTitle('');
    setEditDesc('');
    setEditDue('');
    setEditReminder('');
  };

  const saveEdit = async (): Promise<void> => {
    if (!editId) return;
    const t = editTitle.trim();
    if (!t) return;
    try {
      const r = await api.patch<{ success: boolean; todo: Todo }>(`/todos/${editId}`, {
        title: t,
        description: editDesc.trim() || null,
        due_at: editDue ? new Date(editDue).toISOString() : null,
        reminder_at: editReminder ? new Date(editReminder).toISOString() : null,
      });
      setTodos((prev) => prev.map((x) => x.id === editId ? r.todo : x));
      cancelEdit();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Meine Todos</h1>
        <button
          type="button"
          onClick={() => setQuickOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} /> Neues Todo
        </button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-default overflow-x-auto">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const isIncoming = f.key === 'incoming';
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 text-sm transition-colors border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${
                filter === f.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {Icon && <Icon size={14} />}
              {f.label}
              {isIncoming && incomingCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-danger text-white text-xs font-medium">
                  {incomingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> Lade...
        </div>
      ) : todos.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          Keine Todos in dieser Sicht.
        </div>
      ) : (
        <ul className="space-y-2">
          {todos.map((t) => {
            const isIncoming = filter === 'incoming';
            const isSent = filter === 'sent';
            const isEditable = !isIncoming && !isSent; // nur in „mine"-Tabs editieren
            const isMine = t.owner_user_id === myId;
            return (
            <li
              key={t.id}
              className="rounded-md border border-default bg-card p-3 flex items-start gap-3"
            >
              {isIncoming ? (
                <Inbox size={20} className="mt-1 text-primary flex-shrink-0" />
              ) : isSent ? (
                <Send size={20} className="mt-1 text-muted-foreground flex-shrink-0" />
              ) : (
                <button
                  type="button"
                  onClick={() => void toggle(t.id)}
                  title={t.status === 'done' ? 'Wieder oeffnen' : 'Als erledigt markieren'}
                  className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    t.status === 'done'
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground/40 hover:border-primary'
                  }`}
                >
                  {t.status === 'done' && <Check size={14} />}
                </button>
              )}

              {editId === t.id ? (
                <div className="flex-1 space-y-3">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <textarea
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    rows={2}
                    placeholder="Beschreibung (optional)"
                    className="w-full px-3 py-2 rounded-md border border-default bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <div>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Calendar size={12} /> Faellig
                    </label>
                    <input
                      type="datetime-local"
                      value={editDue}
                      onChange={(e) => setEditDue(e.target.value)}
                      className="w-full sm:w-64 h-10 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Bell size={12} /> Erinnerung
                    </label>
                    <input
                      type="datetime-local"
                      value={editReminder}
                      onChange={(e) => setEditReminder(e.target.value)}
                      className="w-full sm:w-64 h-10 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => void saveEdit()}
                      className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                      <X size={14} /> Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  {isEditable ? (
                    <button
                      type="button"
                      onClick={() => startEdit(t)}
                      className="text-left w-full"
                    >
                      <div className={`font-medium ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>
                        {t.title}
                      </div>
                      {t.description && (
                        <div className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                          {t.description}
                        </div>
                      )}
                    </button>
                  ) : (
                    <div>
                      <div className="font-medium">{t.title}</div>
                      {t.description && (
                        <div className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                          {t.description}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {t.due_at && (
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                        isOverdue(t) ? 'bg-danger/15 text-danger' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Calendar size={12} /> {formatDue(t.due_at)}
                      </span>
                    )}
                    {t.reminder_at && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                        <Bell size={12} /> {formatDue(t.reminder_at)}
                      </span>
                    )}
                    {isSent && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        t.assignment_state === 'pending'  ? 'bg-amber-500/15 text-amber-600'
                        : t.assignment_state === 'accepted' ? 'bg-emerald-500/15 text-emerald-600'
                        : t.assignment_state === 'rejected' ? 'bg-danger/15 text-danger'
                        : 'bg-muted text-muted-foreground'
                      }`}>
                        {t.assignment_state === 'pending'  ? 'wartet' :
                         t.assignment_state === 'accepted' ? 'angenommen' :
                         t.assignment_state === 'rejected' ? 'abgelehnt' : t.assignment_state}
                      </span>
                    )}
                    {isSent && t.assignment_state === 'rejected' && t.rejected_reason && (
                      <span className="text-xs text-muted-foreground italic">„{t.rejected_reason}"</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-1 flex-shrink-0">
                {isIncoming && (
                  <>
                    <button
                      type="button"
                      onClick={() => void accept(t.id)}
                      title="Annehmen"
                      className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm flex items-center gap-1"
                    >
                      <Check size={14} /> Annehmen
                    </button>
                    <button
                      type="button"
                      onClick={() => void reject(t.id)}
                      title="Ablehnen"
                      className="px-2.5 py-1 rounded-md border border-default text-muted-foreground hover:text-danger hover:border-danger text-sm flex items-center gap-1"
                    >
                      <X size={14} /> Ablehnen
                    </button>
                  </>
                )}
                {isEditable && editId !== t.id && (
                  <button
                    type="button"
                    onClick={() => startEdit(t)}
                    title="Bearbeiten"
                    className="p-1.5 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {(isEditable || (isSent && isMine)) && (
                  <button
                    type="button"
                    onClick={() => void del(t.id)}
                    title="Loeschen"
                    className="p-1.5 text-muted-foreground hover:text-danger"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}

      <TodoQuickCaptureModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreated={() => void load()}
      />
    </div>
  );
}

function isOverdue(t: Todo): boolean {
  if (!t.due_at) return false;
  if (t.status === 'done' || t.status === 'cancelled') return false;
  return new Date(t.due_at).getTime() < Date.now();
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(d);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / (24 * 3600 * 1000));
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `heute ${time}`;
  if (diffDays === 1) return `morgen ${time}`;
  if (diffDays === -1) return `gestern ${time}`;
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function toLocalInputValue(iso: string): string {
  // datetime-local-Input erwartet `YYYY-MM-DDTHH:mm` ohne Timezone.
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
